import {
    Box,
    FormControl,
    FormHelperText,
    MenuItem,
    Select,
    Typography,
    styled,
} from "@mui/material";
import { ThemeProperties } from "../../../Theme/ThemeProperties";

// The chrome of the New Work Order form: the card, its horizontal bands, and
// the two bits of text that sit around a field.
//
// SHARED RATHER THAN DUPLICATED. The form is one card split into bands and the
// movement fills three of them, so both WorkOrderForm and MovementBuilder draw
// the same rules and the same label. WorkOrderForm imports MovementBuilder, so
// these cannot live in either without an import cycle.
//
// There is no band TITLE on most of them on purpose: the page reads top to
// bottom as one sentence -- what kind of move, between whom, which assets, and
// why -- and a heading over every pair of fields interrupts it. Only Route
// and the assets band carry one, because those two need a note beside them.

// The tokens the rest of the product uses, in one place so a field added later
// cannot invent its own.
// THE MODULE'S STRUCTURAL TOKENS. Three roles were missing from this object
// and got filled in by literals scattered through the components instead --
// which is how the same role ended up with two or three values that nobody
// chose: TWO dashed placeholder borders (#CFCFDA and antd's own #D9D9D9),
// THREE faint greys for decorative marks (#9A9AA8, #B9B9C6, #C9C9CF) and TWO
// sunken surfaces (#FAFAFC here and #FAFAFA in a component). A missing token
// is not a small gap; it is an invitation to invent one per call site.
export const FORM_TOKENS = {
    // Plain white, named. Not because white is in danger of drifting, but so
    // that "no raw hex outside this file" is a rule with no exceptions -- one
    // permitted literal is the crack the next one comes through.
    surface: "#FFFFFF",
    cardBorder: "#E4E3E3",
    bandRule: "#F0F0F4",
    fieldBorder: "#E6E6E6",
    // The outline of a box holding a value the user CANNOT edit -- the
    // detected source, the stamped movement date. Dashed rather than solid so
    // it does not read as an input, and a step lighter than fieldBorder so it
    // does not read as a disabled one either.
    dashedBorder: "#CFCFDA",
    sunk: "#FAFAFC",
    ink: "#2E2E2E",
    inkSoft: ThemeProperties.greyTextColor,
    inkFaint: "#8D8D8D",
    // A mark that must be VISIBLE but must not compete: the arrow between FROM
    // and TO, the breadcrumb chevron, the monospace paste sample. One step
    // below inkFaint. Not for anything that has to be read carefully.
    inkDecorative: "#9A9AA8",
    // A control that exists but cannot be used right now -- the breadcrumb
    // link while a save is in flight. Distinct from inkDecorative on purpose:
    // one says "not important", the other says "not available", and someone
    // changing one should not change the other.
    disabled: "#B0B0B0",
    // A DIALOG or DRAWER title. One step darker than `ink`, which is body
    // text: a 20px heading set in body ink reads as large body text.
    inkStrong: "#101111",
    // Ordinary body copy in a dialog. It was TEXT_DISABLED_TEXT, chosen
    // because the hex matched -- #494949 either way -- while the meaning did
    // not: whoever changes the product's disabled-text colour would silently
    // restyle the body of four dialogs. FORM_TOKENS.disabled is the token for
    // a control that cannot be used.
    inkBody: "#494949",
    // The label half of a label/value pair, and a table's column headers.
    // Between inkSoft and inkFaint, and the most-used colour in the module --
    // it was written as a literal in twenty-one places.
    inkLabel: "#6B7280",
    // An empty input's prompt. The same value StyledTextField uses, which is
    // the product's own field; this module's two dialogs re-declare the field
    // rather than importing it, and that is how the value got copied.
    placeholder: "#A4B2C1",
    // The ground a detail drawer or a read-only panel sits on. NOT `sunk`,
    // which is a sunken field INSIDE a form -- one is a page surface, the
    // other a control, and they are two steps apart on purpose.
    panel: "#FAFAFA",
    purple: ThemeProperties.dropDownPurple,
    purpleDark: ThemeProperties.purple,
    purpleLight: ThemeProperties.purpleLight,
    // The outline of an outlined PURPLE button -- lighter than the fill so
    // the button does not read as solid.
    purpleBorder: "#D7D9FD",
    danger: "#930500",
    // The required-field asterisk, and only that. Brighter than `danger`
    // because it marks a field rather than reporting a failure, and an
    // asterisk in danger's maroon disappears next to the label it belongs to.
    required: "#D3302F",
};

export const FormCard = ({ children }) => (
    <Box
        sx={{
            bgcolor: "#FFFFFF",
            border: `1px solid ${FORM_TOKENS.cardBorder}`,
            borderRadius: "10px",
            overflow: "hidden",
        }}
    >
        {children}
    </Box>
);

// One horizontal band. The rule belongs to the band ABOVE it, and the last one
// drops it -- so the card never ends on a line with nothing under it.
export const FormBand = ({ children, testId, last = false, sx = {} }) => (
    <Box
        data-testid={testId}
        sx={{
            px: 3,
            py: 2.75,
            borderBottom: last ? "none" : `1px solid ${FORM_TOKENS.bandRule}`,
            ...sx,
        }}
    >
        {children}
    </Box>
);

// A band title with an optional note on the right. The note is where a band
// says something about itself that a field label cannot -- "Source is read
// from the serials you enter below" being the one that earns its place.
export const BandHead = ({ title, note, action, titleColor }) => (
    <Box
        sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
            mb: 2,
        }}
    >
        {/* The colour is the CALLER's only when the caller has something to
            say with it -- the preview's heading turns red once it is counting
            refusals. Everything else keeps the band ink, so a coloured heading
            stays a signal rather than decoration. */}
        <Typography
            sx={{ fontSize: "14px", fontWeight: 700, color: titleColor || FORM_TOKENS.ink }}
        >
            {title}
        </Typography>
        {action ||
            (note ? (
                <Typography sx={{ fontSize: "13px", color: FORM_TOKENS.inkFaint }}>
                    {note}
                </Typography>
            ) : null)}
    </Box>
);

// The label above a control, never a floating MUI label -- the required marker
// is a real red asterisk beside the text, matching every other form in the
// product.
export const FieldLabel = ({ label, required, htmlFor }) => (
    <Box sx={{ display: "flex", alignItems: "center", mb: 0.75 }}>
        <Typography
            component="label"
            htmlFor={htmlFor}
            sx={{ fontSize: "13px", fontWeight: 500, lineHeight: "20px", color: FORM_TOKENS.ink }}
        >
            {label}
        </Typography>
        {required && (
            <Typography
                sx={{
                    fontSize: "13px",
                    fontWeight: 700,
                    lineHeight: "20px",
                    color: FORM_TOKENS.required,
                }}
            >
                &nbsp;*
            </Typography>
        )}
    </Box>
);

// The line under a control. Renders NOTHING for empty text rather than an
// empty element, so a field with no guidance does not reserve a line of space
// that pushes it out of alignment with the field beside it.
export const Helper = ({ text, isError, testId }) =>
    text ? (
        <FormHelperText
            data-testid={testId}
            sx={{
                fontSize: "12px",
                ml: 0,
                mt: 0.75,
                lineHeight: "17px",
                color: isError ? FORM_TOKENS.danger : FORM_TOKENS.inkFaint,
                overflowWrap: "anywhere",
            }}
        >
            {text}
        </FormHelperText>
    ) : null;

// The uppercase caption over each half of the Route panel. Smaller and
// quieter than a field label because the panel is ONE object: "From" and "To"
// name its two ends, they do not label two independent controls.
export const RouteCaption = ({ children }) => (
    <Typography
        sx={{
            fontSize: "11px",
            fontWeight: 500,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: FORM_TOKENS.inkFaint,
            mb: 1.25,
        }}
    >
        {children}
    </Typography>
);

// --------------------------------------------------------------------------
// The product's dropdown
// --------------------------------------------------------------------------
//
// WHY THIS EXISTS AT ALL. These fields were `StyledTextField select`, which is
// the product's TEXT field wearing a select. Three things went wrong with
// that, none of them obvious from the JSX:
//
//   1. StyledTextField's padding rule is `& input`, and a select does not
//      render an <input> -- it renders div.MuiSelect-select. So the 12px/16px
//      padding never applied and the control sat at a different height from
//      every field beside it, on a root that had been told `padding: 0`.
//   2. Its root font is 12px, so the value read smaller than the 14px the
//      rest of the form uses.
//   3. It carries `& .MuiList-root { padding: 0 }`, which is written for a
//      menu list. The Select menu is PORTALED, so that selector can never
//      match from a class on the field -- but it is the rule that would flatten
//      the menu if it ever did, and it is the only menu override anywhere in
//      this path. Nothing here sets MenuProps, so the Paper is MUI's own:
//      rounded and shadowed. Moving off StyledTextField is what takes us out
//      of reach of that rule, without editing a component roughly forty other
//      dialogs share.
//
// The rules below are the "Update Status" select in
// PagesContent/Alerts/IncidentActionDialog.jsx, which is the reference the
// product was measured against. It is DUPLICATED, not imported: that file
// keeps its own private copy and reaching into another page's internals to
// borrow a styled component is worse than two copies. If a third caller wants
// it, this belongs in src/Components/ as one shared control and both sites
// should move -- see the note in the handover.

const SELECT_VALUE_COLOR = "#3D4B5A";
const SELECT_PLACEHOLDER_COLOR = "#A4B2C1";

// ONE FIELD HEIGHT, and it is the product's own.
//
// This form draws three different MUI components -- Select, Autocomplete and
// an antd date picker -- and each ships its own padding, so left alone they
// render three heights and nothing in a two-column row lines up. Movement Type
// sat visibly shorter than the Date & Time field beside it for exactly this
// reason.
//
// The number is DERIVED, not chosen: StyledTextField (the text field every
// other form in the product uses) puts 12px/16px of padding around a 14px/20px
// line, so its content box is 20 + 12 + 12 = 44. Matching it means this form
// is the same size as the rest of the app rather than a fourth opinion.
//
// Every control reads these. Do not write 44 anywhere else.
export const FIELD_METRICS = {
    height: "44px",
    paddingX: "16px",
    paddingY: "12px",
    fontSize: "14px",
    lineHeight: "20px",
    // Room for the 27px caret plus its breathing space, so a long value
    // ellipses instead of running underneath the arrow.
    caretGutter: "44px",
    // Room for a clear button AND the caret. See the clear button rule below.
    clearableGutter: "68px",
};

export const WorkOrderSelect = styled(Select)(() => ({
    // The rendered value is a div, not an input, so MUI's outlined padding
    // lands on .MuiSelect-select and has to be replaced there.
    "& .MuiSelect-select": {
        boxSizing: "border-box",
        height: FIELD_METRICS.height,
        minHeight: FIELD_METRICS.height,
        display: "flex",
        alignItems: "center",
        padding: `${FIELD_METRICS.paddingY} ${FIELD_METRICS.paddingX}`,
        // MUI sets this one with !important to reserve room for its own icon,
        // so matching it is the only way to widen the gutter for a 27px caret.
        paddingRight: `${FIELD_METRICS.caretGutter} !important`,
    },
    "& .MuiOutlinedInput-input .MuiTypography-root": {
        fontSize: FIELD_METRICS.fontSize,
        lineHeight: FIELD_METRICS.lineHeight,
        // One line, ellipsed. A long partner name wrapping would push the
        // control past the height every other field is pinned to.
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    // The product's caret, not MUI's default 24px.
    "& .MuiSvgIcon-root": {
        fontSize: "27px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: FORM_TOKENS.fieldBorder,
        borderWidth: "1px",
        borderRadius: "8px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: FORM_TOKENS.purple,
        borderWidth: "1px",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: FORM_TOKENS.purple,
        borderWidth: "1px",
    },
    // The purple selected state: on focus the VALUE and the caret both turn,
    // not just the outline.
    "&.Mui-focused .MuiOutlinedInput-input .MuiTypography-root": {
        color: FORM_TOKENS.purple,
    },
    "&.Mui-focused .MuiSvgIcon-root": {
        color: FORM_TOKENS.purple,
    },
    // Stated rather than left to specificity. MUI's own .Mui-error rule does
    // win against the base outline above, but "wins by one class" is not a
    // thing a required field's error state should rest on.
    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: "#B50500",
    },
    "&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#B50500",
    },
    "& .Mui-disabled": {
        cursor: "not-allowed",
    },
}));

// ONE MENU, for both controls that have one.
//
// The Select and the Autocomplete render entirely different popups -- a
// MuiMenu with a MuiList, and a MuiAutocomplete paper with its own listbox --
// and both were left on MUI's defaults at different times, so they disagreed
// on radius (4px vs 8px), on listbox padding (8px vs 0) and on how a row is
// inset. Styling one of them made the pair MORE different, not less.
//
// Every number lives here once. Both popups are built from it below, and
// SELECT_ITEM_SX -- which is applied to a MenuItem AND to the Autocomplete's
// option, since renderOption draws the row itself -- reads the same values.
export const WORK_ORDER_MENU = {
    // The radius every other surface on this form uses.
    radius: "8px",
    shadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
    // The listbox owns no padding; the ROWS do. A padded list plus a padded
    // row is two insets nobody chose.
    listPadding: 0,
    optionPaddingY: "8px",
    optionPaddingX: "12px",
    // FROM THE TOKENS TWELVE LINES ABOVE, not re-typed. This object carried
    // its own #EEEFFF and #4D44D8 -- byte-identical to purpleLight and
    // purpleDark in the same file -- plus a fourth purple tint for hover that
    // existed nowhere else.
    hoverBackground: FORM_TOKENS.purpleLight,
    selectedBackground: FORM_TOKENS.purpleLight,
    selectedColor: FORM_TOKENS.purpleDark,
};

// One option row, for BOTH popups. The Select's MenuItem and the
// Autocomplete's option are different elements with different state classes,
// so both vocabularies are spelled out -- `.Mui-selected` is the Select's,
// `[aria-selected]` and `.Mui-focused` are the Autocomplete's.
export const SELECT_ITEM_SX = {
    paddingTop: WORK_ORDER_MENU.optionPaddingY,
    paddingBottom: WORK_ORDER_MENU.optionPaddingY,
    paddingLeft: WORK_ORDER_MENU.optionPaddingX,
    paddingRight: WORK_ORDER_MENU.optionPaddingX,
    // DOUBLE AMPERSAND on every state selector.
    //
    // MenuItem styles its own hover and .Mui-selected from its styled
    // component, at the same specificity a single-& sx produces -- and a tie
    // goes to whichever sheet emotion injected last. That tie is exactly what
    // cost two attempts on the Autocomplete padding. `&&` wins outright and
    // costs nothing, so it is not worth being clever about which way the tie
    // would have fallen.
    "&&:hover": { backgroundColor: WORK_ORDER_MENU.hoverBackground },
    "&&.Mui-focused": { backgroundColor: WORK_ORDER_MENU.hoverBackground },
    "&&.Mui-selected, &&[aria-selected='true']": {
        backgroundColor: WORK_ORDER_MENU.selectedBackground,
        color: WORK_ORDER_MENU.selectedColor,
    },
    "&&.Mui-selected:hover, &&[aria-selected='true']:hover": {
        backgroundColor: WORK_ORDER_MENU.selectedBackground,
    },
};

// The Select's popup, which was still on MUI's defaults while the
// Autocomplete's had been styled. Passed as MenuProps.
export const WORK_ORDER_SELECT_MENU_PROPS = {
    slotProps: {
        paper: {
            sx: {
                borderRadius: WORK_ORDER_MENU.radius,
                boxShadow: WORK_ORDER_MENU.shadow,
                marginTop: "4px",
                "& .MuiList-root": { padding: WORK_ORDER_MENU.listPadding },
            },
        },
    },
};

export const SelectItemText = ({ children, muted = false }) => (
    <Typography
        variant="fw400s16c000000"
        sx={{
            color: muted ? SELECT_PLACEHOLDER_COLOR : SELECT_VALUE_COLOR,
            lineHeight: "20px",
        }}
    >
        {children}
    </Typography>
);

// A whole dropdown: label-less, placeholder-driven, and the only select shape
// this form has.
//
// `options` is [{value, label}]. MenuItem is rendered as a DIRECT child of the
// Select -- MUI clones its children to wire click and value handling, so a
// wrapper component around MenuItem would render the row and leave it
// unselectable.
export const WorkOrderSelectField = ({
    value = "",
    onChange,
    options = [],
    placeholder = "Select",
    disabled = false,
    error = false,
    testId,
}) => (
    <FormControl fullWidth error={Boolean(error)}>
        <WorkOrderSelect
            value={value}
            onChange={onChange}
            disabled={disabled}
            error={Boolean(error)}
            // The placeholder is a rendered VALUE rather than a disabled
            // first MenuItem: a placeholder sitting in the menu is an option
            // the user can hover and try to pick.
            displayEmpty
            MenuProps={WORK_ORDER_SELECT_MENU_PROPS}
            inputProps={{
                "data-testid": testId,
                "aria-label": placeholder,
            }}
            renderValue={(selected) => {
                const chosen = (options || []).find(
                    (option) => option?.value === selected
                );
                return (
                    <SelectItemText muted={!chosen}>
                        {chosen ? chosen.label : placeholder}
                    </SelectItemText>
                );
            }}
        >
            {(options || []).map((option) => (
                <MenuItem key={option.value} value={option.value} sx={SELECT_ITEM_SX}>
                    <SelectItemText>{option.label}</SelectItemText>
                </MenuItem>
            ))}
        </WorkOrderSelect>
    </FormControl>
);

// The Autocomplete half of the same vocabulary.
//
// The entity pickers cannot be Selects -- they hold up to a hundred partners
// and are searched -- but they sit in the same panel as the dropdowns and must
// read as the same control: same outline, same focus purple on the value and
// the caret, same 27px caret, same option row.
// THE CLEAR BUTTON IS GONE, at the call site, via disableClearable.
//
// It rendered as an X in a grey hover circle crowding the caret, and it is an
// action these fields do not have: destination and vendor are required, and
// the way to change one is to pick another. An empty required field is not a
// state the form can do anything with.
//
// What is left is the caret, and it has to BE the Select's caret -- same 27px,
// same colour, same purple on focus -- because the two controls sit in one
// panel and a user reading down it should not be able to tell which is which.
// MUI wraps it in an IconButton, so the button's own padding and hover circle
// are removed rather than restyled: a Select's arrow is not a button and this
// one must not look like one.
// DOUBLE AMPERSAND ON THE TWO RULES BELOW, and it is load-bearing.
//
// MUI styles .MuiAutocomplete-inputRoot and .MuiAutocomplete-endAdornment from
// the Autocomplete's own styled component, at the same specificity a single-&
// sx produces. A tie goes to whichever stylesheet emotion injected last, and
// MUI won. The result was measured in the browser rather than guessed:
//
//   padding  ->  6px 0 6px 6px   (MUI's) instead of  0 44px 0 16px  (ours)
//   caret    ->  top: calc(50% - 14px)  (MUI's)  PLUS  translateY(-50%) (ours)
//
// That second line is the one that looked broken: our transform applied because
// MUI sets none, while our `top` did not -- so the two combined to -5.5px and
// hung the caret above the field. Half a rule is worse than none.
//
// `&&` doubles the generated class and wins outright. With it the padding
// matches the Select exactly and the caret centre sits 0.00px from the field
// centre, both verified against the rendered element.
export const WORK_ORDER_AUTOCOMPLETE_SX = {
    "&& .MuiAutocomplete-inputRoot": {
        padding: `0 ${FIELD_METRICS.caretGutter} 0 ${FIELD_METRICS.paddingX}`,
    },
    "& .MuiAutocomplete-popupIndicator": {
        padding: 0,
        marginRight: 0,
        "&:hover": { backgroundColor: "transparent" },
        "& .MuiSvgIcon-root": { fontSize: "27px" },
    },
    "& .Mui-focused .MuiAutocomplete-popupIndicator .MuiSvgIcon-root": {
        color: FORM_TOKENS.purple,
    },
    // !important, and it is not decoration. `&&` is enough for the padding
    // rule above and is NOT enough here: MUI's endAdornment rule ties with it
    // and wins, so `top` stayed at MUI's calc(50% - 14px) while our transform
    // applied on top of it -- leaving the caret 14px ABOVE centre. Half a rule
    // landing is worse than none, and it is the second time this exact pair
    // has done it.
    //
    // Measured after this change, on both Route pickers: offset from the
    // field centre 0.00px. Re-measure if you touch it; reading it is what got
    // it wrong twice.
    "&& .MuiAutocomplete-endAdornment": {
        right: "12px !important",
        top: "50% !important",
        transform: "translateY(-50%) !important",
    },
};

// The listbox is PORTALED, so nothing in the sx above can reach it -- which is
// why the option rows kept MUI's 6px/16px while the Select's menu used ours.
// These go through slotProps instead, and carry the same numbers SELECT_ITEM_SX
// gives a MenuItem so the two menus are one menu.
export const WORK_ORDER_AUTOCOMPLETE_SLOT_PROPS = {
    paper: {
        sx: {
            // OPEN THE SAME WAY THE SELECT DOES.
            //
            // A Select's menu is wrapped in MUI's Grow -- it scales up from
            // 0.75 and fades in. An Autocomplete's Popper has NO transition at
            // all, so its list simply appears. Side by side in one panel, the
            // two controls felt like different applications.
            //
            // Grow's own values, as a keyframe: scale(0.75) to scale(1) with
            // opacity, on MUI's standard easing, over the 250ms Grow uses for
            // a list this size. transformOrigin top means it grows downward
            // from the field rather than out of its own middle, which is what
            // Grow does when the menu opens below.
            "@keyframes workOrderMenuGrow": {
                from: { opacity: 0, transform: "scale(0.75)" },
                to: { opacity: 1, transform: "scale(1)" },
            },
            transformOrigin: "top",
            animation: "workOrderMenuGrow 250ms cubic-bezier(0.4, 0, 0.2, 1)",
            // A viewer who has asked for less motion gets the list, not the
            // animation -- the transition is polish and never information.
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            borderRadius: WORK_ORDER_MENU.radius,
            boxShadow: WORK_ORDER_MENU.shadow,
            "& .MuiAutocomplete-listbox": { padding: WORK_ORDER_MENU.listPadding },
            // Double ampersand: MUI styles the option from the Autocomplete's
            // own styled component and wins a single-& tie. Same lesson as the
            // inputRoot padding.
            "&& .MuiAutocomplete-option": {
                paddingTop: WORK_ORDER_MENU.optionPaddingY,
                paddingBottom: WORK_ORDER_MENU.optionPaddingY,
                paddingLeft: WORK_ORDER_MENU.optionPaddingX,
                paddingRight: WORK_ORDER_MENU.optionPaddingX,
            },
            "&& .MuiAutocomplete-option:hover, && .MuiAutocomplete-option.Mui-focused": {
                backgroundColor: WORK_ORDER_MENU.hoverBackground,
            },
            "&& .MuiAutocomplete-option[aria-selected='true']": {
                backgroundColor: WORK_ORDER_MENU.selectedBackground,
                color: WORK_ORDER_MENU.selectedColor,
            },
        },
    },
    // MUI renders title={open ? closeText : openText} on the caret, so an
    // ordinary dropdown arrow grew a native "Open"/"Close" tooltip that no
    // other control on the page has.
    popupIndicator: { title: "" },
};

// THE CLEAR BUTTON RULE, for every Autocomplete in this flow.
//
//   freeSolo text fields   -> KEEP the clear button
//   entity pickers          -> disableClearable
//
// The distinction is what clearing MEANS. A serial is something the maker
// typed, and typed into the wrong row often enough that wiping it in one
// action is worth a control. A destination or a vendor is something they
// PICKED from a list, it is required, and the way to change it is to pick
// another -- so an X there offers a state the form cannot use and crowds the
// caret to offer it.
//
// Both pickers in the Route panel therefore pass disableClearable, and the
// single-mode serial picker does not. If a third Autocomplete arrives, decide
// which of the two it is rather than copying whichever one is nearest.
//
// A clearable field carries TWO buttons in its end adornment, so it needs a
// wider gutter than the measured single-caret one. Kept as its own export
// rather than by widening the shared gutter: the entity pickers' padding was
// measured against the rendered element and must not move.
export const WORK_ORDER_CLEARABLE_AUTOCOMPLETE_SX = {
    ...WORK_ORDER_AUTOCOMPLETE_SX,
    "&& .MuiAutocomplete-inputRoot": {
        padding: `0 ${FIELD_METRICS.clearableGutter} 0 ${FIELD_METRICS.paddingX}`,
    },
    "& .MuiAutocomplete-clearIndicator": {
        padding: "2px",
        marginRight: "2px",
        "& .MuiSvgIcon-root": { fontSize: "18px" },
    },
};

// The antd date picker, dragged into the form's own palette.
//
// SCOPED TO THIS FORM, applied to a Box that wraps the control, because
// SingleDateTimePicker is shared -- RecordReplacement renders the same
// component and must keep antd's defaults. Changing the component would have
// changed both.
//
// antd's own values, and what they are replaced with:
//
//   .ant-picker                     #d9d9d9  ->  #E6E6E6   (the form's border)
//   .ant-picker:hover               #4F44E0  ->  #6D72F6
//   focus                           left to antd, tinted by colorPrimary --
//                                     see the note in the object below
//   .ant-picker-input > input       14px, antd's text colour -> #3D4B5A
//   ::placeholder                   ->  #A4B2C1, the form's placeholder
//
// The POPUP is portaled and none of these can reach it; its accent comes from
// the colorPrimary prop instead.
export const WORK_ORDER_DATE_FIELD_SX = {
    "& .ant-picker": {
        borderColor: FORM_TOKENS.fieldBorder,
        borderRadius: WORK_ORDER_MENU.radius,
    },
    "& .ant-picker:hover": { borderColor: FORM_TOKENS.purple },

    // NO FOCUS RULE HERE, and that is the fix rather than an omission.
    //
    // There was one, on `.ant-picker-focused`, and it also set
    // `boxShadow: none` to drop antd's glow. Measured in the browser: antd v5
    // NEVER ADDS THAT CLASS. Clicking the field leaves the class list at
    // `ant-picker ant-picker-outlined css-dev-only-...`, unchanged, and the
    // element does not match `:focus-within` either.
    //
    // So the rule was dead, the glow was gone, and the field had no focus
    // indication at all -- the one state a keyboard user needs most. Removing
    // an indicator is only safe if the replacement lands, and this one could
    // not.
    //
    // antd's own focus styling now shows through, tinted by the `colorPrimary`
    // prop this form passes to SingleDateTimePicker. That is a glow where the
    // MUI fields draw a border, which is a small inconsistency and a much
    // smaller one than no focus state.
    "& .ant-picker .ant-picker-input > input": {
        fontSize: FIELD_METRICS.fontSize,
        lineHeight: FIELD_METRICS.lineHeight,
        color: "#3D4B5A",
    },
    "& .ant-picker .ant-picker-input > input::placeholder": {
        color: "#A4B2C1",
        opacity: 1,
    },
    // antd marks an invalid field with its own red; the form uses one red.
    "& .ant-picker-status-error.ant-picker": { borderColor: "#B50500" },
};

export const WORK_ORDER_AUTOCOMPLETE_INPUT_SX = {
    "& .MuiOutlinedInput-root": {
        backgroundColor: "#FFFFFF",
        // The same 44px every other control on this form is pinned to. The
        // padding moves to the ROOT and off the input, because the root is the
        // flex box that centres the value.
        boxSizing: "border-box",
        height: FIELD_METRICS.height,
        padding: `0 ${FIELD_METRICS.caretGutter} 0 ${FIELD_METRICS.paddingX}`,
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: FORM_TOKENS.fieldBorder,
            borderWidth: "1px",
            borderRadius: "8px",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: FORM_TOKENS.purple,
            borderWidth: "1px",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: FORM_TOKENS.purple,
            borderWidth: "1px",
        },
        "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "#B50500" },
        "& input": {
            fontSize: FIELD_METRICS.fontSize,
            lineHeight: FIELD_METRICS.lineHeight,
            color: SELECT_VALUE_COLOR,
            height: FIELD_METRICS.lineHeight,
            // !important because MUI's Autocomplete sets the input padding
            // from the root's own styled rule, at a specificity an sx on the
            // TextField cannot beat. This is the one declaration that has to
            // win, and losing it is what made these fields short.
            padding: "0 !important",
        },
        "&.Mui-focused input": { color: FORM_TOKENS.purple },
        "& input::placeholder": {
            color: SELECT_PLACEHOLDER_COLOR,
            opacity: 1,
        },
    },
};

// The segmented control that picks how assets are entered.
//
// Two options, one visible choice, no dropdown: a select would hide the
// alternative behind a click and make "there is another way to do this"
// something the user has to go looking for. It is the control the approved
// mockup put above this box, in the product's tokens -- 8px radius, the field
// border, and the purple-light fill for the side that is on.
export const SegmentedControl = ({ value, options = [], onChange, disabled = false }) => (
    <Box
        sx={{
            display: "inline-flex",
            border: `1px solid ${FORM_TOKENS.fieldBorder}`,
            borderRadius: "8px",
            overflow: "hidden",
            opacity: disabled ? 0.6 : 1,
        }}
    >
        {options.map((option, index) => {
            const active = option.value === value;
            return (
                <Box
                    key={option.value}
                    component="button"
                    type="button"
                    // A toggle button, not a tab: it switches how one field
                    // is entered rather than which panel is shown, and
                    // aria-pressed is the property that says so.
                    aria-pressed={active}
                    data-testid={option.testId}
                    disabled={disabled}
                    onClick={() => onChange?.(option.value)}
                    sx={{
                        font: "inherit",
                        fontSize: "13px",
                        fontWeight: 500,
                        fontFamily: "Roboto",
                        padding: "7px 14px",
                        border: 0,
                        borderLeft:
                            index > 0 ? `1px solid ${FORM_TOKENS.fieldBorder}` : 0,
                        cursor: disabled ? "not-allowed" : "pointer",
                        whiteSpace: "nowrap",
                        backgroundColor: active ? FORM_TOKENS.purpleLight : "#FFFFFF",
                        color: active ? FORM_TOKENS.purpleDark : FORM_TOKENS.inkSoft,
                        "&:hover": {
                            backgroundColor: active
                                ? FORM_TOKENS.purpleLight
                                : "#FAFAFC",
                        },
                    }}
                >
                    {option.label}
                </Box>
            );
        })}
    </Box>
);
